import { SearchInput } from "@c/shared/SearchInput";
import { cn } from "@u/cn";
import Button from "@ui/button";
import { Download, RefreshCw } from "lucide-react";
import { isValidElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DashboardActiveFilters } from "./DashboardActiveFilters";
import { DashboardFilterPanel } from "./DashboardFilterPanel";
import type { ActiveFilterChip } from "./activeFilterChips";

export interface DashboardToolbarSearch {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export interface DashboardToolbarProps {
  /**
   * Either a search config (renders a `SearchInput`) or a ready-made node
   * (collection pages supply their own input). Config form is used by the DS
   * data-display shells (List/Table/Grid); node form by collection pages.
   */
  search?: DashboardToolbarSearch | ReactNode;
  /** Slot rendered immediately to the left of the search input (e.g. select-all). */
  searchLeading?: ReactNode;
  /** Slot rendered on the right of the search row (e.g. export, sort, view toggle). */
  searchTrailing?: ReactNode;
  filters?: ReactNode;
  /**
   * Panel surface only: render `filters` at the end of the search row on
   * desktop (mobile keeps them on their own row below).
   */
  filtersInline?: boolean;
  /**
   * Sheet content for the mobile filter button (page surface only). Use
   * pill/chip sections here. Falls back to `filters` when omitted.
   */
  mobileFilters?: ReactNode;
  sort?: ReactNode;
  actions?: ReactNode;
  onRefresh?: () => void;
  onExport?: () => void;
  createButton?: ReactNode;
  bulkActions?: ReactNode;
  selectedCount?: number;
  /** Active-filter chips bar (page surface). Omit any of the three to skip it. */
  activeChips?: ActiveFilterChip[];
  onRemoveChip?: (field: string, value: string) => void;
  onClearChips?: () => void;
  /**
   * `panel` (default): bordered toolbar nested inside a DS data-display panel.
   * `page`: top-level collection scaffold (tours/venues/bookings) — filters
   * collapse behind a single "Filtros (N)" button at EVERY width (a permanent
   * grid of 5-8 dropdowns dominated the page and pushed the results down), and
   * what is actually applied shows as removable chips in the bar below.
   */
  surface?: "panel" | "page";
  /** Rendered after the bar (page surface). */
  children?: ReactNode;
  className?: string;
}

function isSearchConfig(
  search: DashboardToolbarProps["search"],
): search is DashboardToolbarSearch {
  return (
    !!search &&
    !isValidElement(search) &&
    typeof search === "object" &&
    "onChange" in (search as object)
  );
}

export function DashboardToolbar({
  search,
  searchLeading,
  searchTrailing,
  filters,
  filtersInline,
  mobileFilters,
  sort,
  actions,
  onRefresh,
  onExport,
  createButton,
  bulkActions,
  selectedCount = 0,
  activeChips,
  onRemoveChip,
  onClearChips,
  surface = "panel",
  children,
  className,
}: DashboardToolbarProps) {
  const { t } = useTranslation();

  if (selectedCount > 0 && bulkActions) {
    return (
      <div
        className={cn(
          "flex items-center justify-between gap-3 px-6 py-3 border-b border-black/5 dark:border-white/5 bg-muted/40",
          className,
        )}
      >
        <p className="label-micro">
          {t("ds_selected_count", {
            count: selectedCount,
            defaultValue: "{{count}} selected",
          })}
        </p>
        <div className="flex items-center gap-2">{bulkActions}</div>
      </div>
    );
  }

  const searchNode = isSearchConfig(search) ? (
    <SearchInput
      value={search.value}
      onChange={search.onChange}
      placeholder={search.placeholder}
      className="w-full"
    />
  ) : (
    search
  );

  // Page surface: top-level collection scaffold. Filters collapse behind a
  // mobile sheet (count badge from active chips) and a removable chips bar
  // sits under the bar. Owned here so every collection gets the same behavior.
  if (surface === "page") {
    const activeCount = activeChips?.length ?? 0;
    return (
      <div className={cn("space-y-3", className)}>
        <div className="flex flex-col gap-3 pb-6 border-b border-border/20">
          <div className="flex items-center gap-2">
            {searchLeading}
            {/* Search is the flexible element (shrinks to fit); the controls
                stay whole and never overflow a narrow card. */}
            <div className="min-w-0 flex-1">{searchNode}</div>
            <div className="flex shrink-0 items-center gap-2">
              {filters && (
                <DashboardFilterPanel
                  activeCount={activeCount}
                  className="sm:h-9"
                >
                  {mobileFilters ?? filters}
                </DashboardFilterPanel>
              )}
              {searchTrailing}
            </div>
          </div>
        </div>

        {activeChips && onRemoveChip && onClearChips && (
          <DashboardActiveFilters
            chips={activeChips}
            onRemove={onRemoveChip}
            onClear={onClearChips}
          />
        )}

        {children}
      </div>
    );
  }

  const hasSearchRow = !!(searchLeading || searchNode || searchTrailing);
  const hasControlsRow = !!(
    filters ||
    sort ||
    actions ||
    onRefresh ||
    onExport ||
    createButton
  );

  // Nothing to render: the collection drives its controls from elsewhere (e.g. a
  // page-level toolbar above a master/detail split). Don't leave an empty bar.
  if (!hasSearchRow && !hasControlsRow) return null;

  // Panel surface: search sits on its own row so the filters/actions row gets
  // full width to breathe (filters don't compete with the search box for space).
  return (
    <div
      className={cn(
        "flex flex-col gap-3 px-6 py-4 border-b border-black/5 dark:border-white/5",
        className,
      )}
    >
      {hasSearchRow && (
        <div className="flex items-center gap-2 w-full">
          <div className="flex items-center gap-2 min-w-0 flex-1 sm:max-w-sm">
            {searchLeading}
            {searchNode}
          </div>
          {filtersInline && filters && (
            <div className="ml-auto hidden sm:flex flex-wrap items-center justify-end gap-2">
              {filters}
            </div>
          )}
          {searchTrailing && (
            <div className="ml-auto flex min-w-0 items-center gap-2">
              {searchTrailing}
            </div>
          )}
        </div>
      )}
      {hasControlsRow && (
        <div
          className={cn(
            "flex flex-col sm:flex-row sm:items-center gap-3",
            // Inline filters: this row only exists for mobile filters unless
            // there are other desktop controls.
            filtersInline &&
              !(sort || actions || onRefresh || onExport || createButton) &&
              "sm:hidden",
          )}
        >
          {filtersInline ? (
            filters && <div className="sm:hidden">{filters}</div>
          ) : (
            filters
          )}
          {sort}
          <div className="flex items-center gap-2 sm:ml-auto">
            {actions}
            {onRefresh && (
              <Button
                design="outline"
                size="sm"
                icon={RefreshCw}
                label={t("ds_refresh", { defaultValue: "Refresh" })}
                responsiveLabel
                onClick={onRefresh}
                className="rounded-xl text-xs h-8"
              />
            )}
            {onExport && (
              <Button
                design="outline"
                size="sm"
                icon={Download}
                label={t("ds_export", { defaultValue: "Export" })}
                responsiveLabel
                onClick={onExport}
                className="rounded-xl text-xs h-8"
              />
            )}
            {createButton}
          </div>
        </div>
      )}
    </div>
  );
}
